// src/pages/Chat/VoiceRecorder.jsx
import React, { useState, useRef, useEffect, useCallback } from "react";
import { FiTrash2, FiSend } from "react-icons/fi";

export default function VoiceRecorder({ onStop, onCancel }) {
  const [duration, setDuration] = useState(0);
  const [audioBlob, setAudioBlob] = useState(null);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
      if (timerRef.current) clearInterval(timerRef.current);
    }
  }, []);

  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        setAudioBlob(blob);
        stream.getTracks().forEach((track) => track.stop());
      };

      mediaRecorder.start();
      timerRef.current = setInterval(() => {
        setDuration((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error("Error accessing microphone:", err);
      let errMsg = "Microphone access denied. Please check your browser permissions and ensure you are using a secure connection (HTTPS).";
      if (!navigator.mediaDevices) {
        errMsg = "Microphone access is blocked. This feature requires a secure connection (HTTPS) or localhost.";
      } else if (err.name === "NotAllowedError" || err.name === "PermissionDeniedError") {
        errMsg = "Microphone permission was denied. Please enable it in your browser settings for this site.";
      } else if (err.name === "NotFoundError" || err.name === "DevicesNotFoundError") {
        errMsg = "No microphone found on your device.";
      }
      alert(errMsg);
      onCancel();
    }
  }, [onCancel]);

  useEffect(() => {
    startRecording();
    return () => {
      stopRecording();
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [startRecording, stopRecording]);

  const handleSend = () => {
    if (audioBlob) {
      onStop(audioBlob);
    } else {
      // If still recording, stop and then send
      mediaRecorderRef.current.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: "audio/webm" });
        onStop(blob);
      };
      stopRecording();
    }
  };

  const formatDuration = (sec) => {
    const mins = Math.floor(sec / 60);
    const secs = sec % 60;
    return `${mins}:${secs.toString().padStart(2, "0")}`;
  };

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-slate-800/80 backdrop-blur-md rounded-2xl border border-blue-500/30 animate-in fade-in slide-in-from-bottom-2">
      <div className="flex items-center gap-2">
        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse" />
        <span className="text-xs font-mono text-slate-200">{formatDuration(duration)}</span>
      </div>
      
      <div className="flex-1 h-8 flex items-center justify-center">
        {/* Placeholder for visualizer or simple wave */}
        <div className="flex gap-0.5 items-end h-4">
          {[...Array(12)].map((_, i) => (
            <div 
              key={i} 
              className="w-1 bg-blue-500/50 rounded-full animate-bounce" 
              style={{ 
                height: `${Math.random() * 100}%`,
                animationDelay: `${i * 0.1}s`,
                animationDuration: '0.6s'
              }} 
            />
          ))}
        </div>
      </div>

      <div className="flex items-center gap-1">
        <button
          onClick={onCancel}
          className="p-2 rounded-full text-slate-500 hover:text-red-400 hover:bg-red-400/10 transition-colors"
          title="Cancel"
        >
          <FiTrash2 size={16} />
        </button>
        <button
          onClick={handleSend}
          className="p-2 rounded-full bg-blue-600 text-white hover:bg-blue-500 transition-all shadow-lg shadow-blue-600/20"
          title="Send Voice Note"
        >
          <FiSend size={16} />
        </button>
      </div>
    </div>
  );
}
